// Products & stock: low stock first, and a checked restock/adjust change.

/** @typedef {{ productId: string; name: string; line: import('@popcart/core/v1').ProductLine; available: number; onHand: number }} StockRow */

/** At or below this many available, a product counts as running low. */
export const LOW_STOCK_AT = 5;

/** @param {StockRow} r @returns {'sold_out' | 'low' | 'ok'} */
export function stockLevel(r) {
  if (r.available <= 0) return 'sold_out';
  if (r.available <= LOW_STOCK_AT) return 'low';
  return 'ok';
}

const RANK = { sold_out: 0, low: 1, ok: 2 };

/** Sold out, then low (fewest first), then the rest by name. @param {StockRow[]} rows */
export function sortStock(rows) {
  return rows.slice().sort((a, b) => {
    const r = RANK[stockLevel(a)] - RANK[stockLevel(b)];
    if (r) return r;
    if (stockLevel(a) !== 'ok' && a.available !== b.available) return a.available - b.available;
    return a.name.localeCompare(b.name);
  });
}

/**
 * Validate a stock change before it's sent. Restock only adds; adjust may go either way but
 * never below zero on hand. A note is required when taking stock away (shrinkage, spoilage).
 * @param {{ delta: number; reason: 'restock' | 'adjust'; note?: string; onHand: number }} c
 * @returns {{ ok: true; delta: number; reason: 'restock' | 'adjust'; note?: string } | { ok: false; error: string }}
 */
export function validateStockChange(c) {
  if (!Number.isInteger(c.delta) || c.delta === 0) return { ok: false, error: 'Choose how many to add or remove.' };
  if (Math.abs(c.delta) > 10000) return { ok: false, error: 'That is more than 10,000 at once. Check the number.' };
  if (c.reason !== 'restock' && c.reason !== 'adjust') return { ok: false, error: 'Pick a reason.' };
  if (c.reason === 'restock' && c.delta < 0) return { ok: false, error: 'A restock adds stock. Use "Correction" to remove.' };
  if (c.onHand + c.delta < 0) return { ok: false, error: `Only ${c.onHand} on hand. You can remove at most ${c.onHand}.` };
  const note = (c.note || '').trim();
  if (c.delta < 0 && !note) return { ok: false, error: 'Say why stock is being removed (for example: spoiled).' };
  if (note.length > 200) return { ok: false, error: 'Keep the note under 200 characters.' };
  return note ? { ok: true, delta: c.delta, reason: c.reason, note } : { ok: true, delta: c.delta, reason: c.reason };
}
